import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, AUTH } from "@/lib/api";
import AuthShell from "@/components/AuthShell";
import SiteFooter from "@/components/SiteFooter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { CheckCircle2, Circle, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Onboard() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [tenant, setTenant] = useState(null);
  const [plan, setPlan] = useState("establishment");
  const [locations, setLocations] = useState(2);
  const [installment, setInstallment] = useState(false);
  const [feeMode, setFeeMode] = useState("diner_150");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const t = await api.get(`/tenants/${slug}/admin`);
    setTenant(t.data);
    if (t.data.fee_mode) setFeeMode(t.data.fee_mode);
    if (t.data.subscription_plan) setPlan(t.data.subscription_plan);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  useEffect(() => {
    if (sp.get("go_live") === "success") toast.success("Subscription active — your storefront is live");
  }, [sp]);

  // Returned from Stripe-hosted onboarding → verify the account can take charges.
  useEffect(() => {
    if (sp.get("stripe") === "return") completeStripe();
    else if (sp.get("stripe") === "refresh")
      toast.message("Stripe onboarding was interrupted — click Connect Stripe to resume.");
    /* eslint-disable-next-line */
  }, [sp]);

  const startStripe = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${slug}/stripe/connect`);
      const url = r?.data?.onboarding_url;
      if (!url) {
        toast.error("Could not start Stripe onboarding — please try again.");
        return;
      }
      // Redirect to Stripe's hosted onboarding. Stripe returns the owner to
      // /onboard/:slug?stripe=return, where the effect below verifies the account.
      toast.message("Redirecting to Stripe…");
      window.location.assign(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe onboarding failed to start.");
      setBusy(false);
    }
    // note: no finally — on success we navigate away, so leave busy=true.
  };

  const completeStripe = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${slug}/stripe/complete`);
      if (r?.data?.ok) {
        toast.success("Stripe verified — you can accept payments.");
      } else {
        toast.error("Stripe onboarding isn't finished yet. Complete all steps, then verify again.");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify Stripe status.");
    } finally { setBusy(false); }
  };

  const goLive = async () => {
    if (!AUTH.token()) { toast.error("Sign in first"); nav("/login"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tenants/${slug}/go-live`, {
        subscription_plan: plan, location_count: locations,
        setup_installment: installment, fee_mode: feeMode,
      }, { headers: { origin: window.location.origin }});
      if (r.data.invoice_url) {
        window.location.href = r.data.invoice_url;
      } else {
        nav(`/onboard/${slug}?go_live=success`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start checkout");
      setBusy(false);
    }
  };

  if (!tenant) return <div className="p-12">Loading...</div>;

  const monthly = plan === "establishment" ? 179 : 99 * Math.max(2, locations);
  const setupToday = installment ? 150 : 899;
  const dueToday = monthly + setupToday;

  const Step = ({ done, label, sub }) => (
    <div className="flex items-start gap-3 py-3">
      {done ? <CheckCircle2 className="w-5 h-5 text-primary mt-0.5"/> : <Circle className="w-5 h-5 text-muted-foreground mt-0.5"/>}
      <div>
        <div className={done ? "font-bold" : "font-bold text-muted-foreground"}>{label}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </div>
    </div>
  );

  return (
    <div>
      <AuthShell />
      <section className="ds-container py-12">
        <p className="overline mb-3">activation · {tenant.name}</p>
        <h1 className="text-5xl mb-2"><span className="font-script text-primary text-6xl font-normal block leading-[0.7] mb-2">Go live</span> in three steps.</h1>
        <p className="text-muted-foreground mb-10">Once these are done, we flip your storefront from preview to public.</p>

        <div className="grid lg:grid-cols-5 gap-6">
          <Card className="p-7 lg:col-span-3">
            <Step done={true} label="Account & business created" sub={`/${slug}`} />
            <div className="editorial-divider"/>
            <Step done={!!tenant.stripe_connected_account_id}
                  label="Connect Stripe"
                  sub={tenant.stripe_connected_account_id || "Stripe Express account for instant payouts"} />
            <div className="ml-8 mt-2 mb-2 flex gap-2">
              {!tenant.stripe_connected_account_id ? (
                <Button data-testid="onboard-stripe-start" onClick={startStripe} disabled={busy} size="sm">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : "Connect Stripe"}
                </Button>
              ) : !tenant.stripe_onboarded ? (
                <Button data-testid="onboard-stripe-complete" onClick={completeStripe} disabled={busy} size="sm">
                  Verify Stripe status
                </Button>
              ) : <span className="text-xs text-primary font-bold">Verified ✓</span>}
            </div>
            <div className="editorial-divider"/>
            <Step done={tenant.subscription_status === "active"}
                  label="Pick plan & pay first month + setup"
                  sub={tenant.subscription_status === "active" ? "Active subscription" : "Establishment $179/mo or Enterprise $99/loc"} />
          </Card>

          <div className="lg:col-span-2 space-y-4">
            <Card className="p-6">
              <p className="overline mb-3">plan</p>
              {[["establishment","Establishment · $179/mo","Single location"],
                ["enterprise",`Enterprise · $99/loc/mo`,"2+ locations"]].map(([k,price,desc]) => (
                <label key={k}
                       data-testid={`onboard-plan-${k}`}
                       className={`block cursor-pointer mb-2 p-3 rounded-lg border-2 transition-colors ${plan===k?"border-primary bg-primary/5":"border-border hover:border-primary/50"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-bold capitalize text-sm">{price}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">{desc}</div>
                  <input type="radio" name="plan" value={k} checked={plan===k} onChange={()=>setPlan(k)} className="hidden"/>
                </label>
              ))}
              {plan === "enterprise" && (
                <div className="mt-2">
                  <label className="text-xs font-bold mb-1.5 block">Locations</label>
                  <input type="number" min={2} value={locations} onChange={e=>setLocations(parseInt(e.target.value)||2)}
                         className="w-full px-3 py-2 border rounded-md text-sm" data-testid="onboard-locations"/>
                </div>
              )}
            </Card>

            <Card className="p-6">
              <p className="overline mb-3">setup fee</p>
              <div className="flex items-center justify-between rounded-md border border-border p-3 mb-1">
                <div>
                  <div className="text-sm font-bold">{installment ? "$150 × 6 mo" : "$899 once"}</div>
                  <div className="text-[11px] text-muted-foreground">{installment ? "Lower upfront cost" : "Save vs installment"}</div>
                </div>
                <Switch checked={installment} onCheckedChange={setInstallment} data-testid="onboard-installment"/>
              </div>
            </Card>

            <Card className="p-6">
              <p className="overline mb-3">transaction fee</p>
              {[["diner_150","Pass $1.50 to diner","Most popular · net-zero overhead"],
                ["platform_1pct","1% absorbed by you","Cleaner diner checkout"]].map(([k,t,d]) => (
                <label key={k}
                       data-testid={`onboard-fee-${k}`}
                       className={`block cursor-pointer mb-2 p-3 rounded-lg border-2 transition-colors ${feeMode===k?"border-primary bg-primary/5":"border-border hover:border-primary/50"}`}>
                  <div className="text-sm font-bold">{t}</div>
                  <div className="text-xs text-muted-foreground">{d}</div>
                  <input type="radio" name="fee" value={k} checked={feeMode===k} onChange={()=>setFeeMode(k)} className="hidden"/>
                </label>
              ))}
            </Card>

            <Card className="p-6 tracing-beam">
              <p className="overline mb-3">due today</p>
              <div className="text-4xl font-extrabold mb-1">${dueToday.toFixed(0)}</div>
              <div className="text-xs text-muted-foreground mb-4">
                ${monthly}/mo subscription{installment ? " + $150/mo setup × 6" : " + $899 one-time setup"}
              </div>
              <Button data-testid="onboard-go-live" disabled={!tenant.stripe_onboarded || busy} onClick={goLive} className="w-full h-11 font-bold">
                {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <>Go live <ArrowRight className="w-4 h-4 ml-1"/></>}
              </Button>
              {!tenant.stripe_onboarded && <p className="text-[11px] text-muted-foreground mt-2 text-center">Connect Stripe first</p>}
            </Card>
          </div>
        </div>
      </section>
      <SiteFooter />
    </div>
  );
}
